import { EventType } from '@graphql/generated-base';

import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventFeatureFlagsContext, useIsFeatureEnabled } from '@shared/core/featureFlags';

export enum EventFeature {
  // use the new event admin invite workflow that has no legacy dependencies
  adminInvite,
  // enable access to carbon integration
  // This will also hide wedding specific features for event type wedding
  carbonEnabled,
  //enables cash funds messaging type
  cashFundMessaging,
  // Disables showing the website page Add App
  disableAddAppPage,
  // disable UX that exposes links to legacy RSVP route and behavior
  disableLegacyRsvp,
  // disable settings and features that are reliant on legacy system
  disableLegacyWebFeatures,
  // Disables wedding specific partner 1 and partner 2 name inputs in settings
  disableSettingPersonalInfo,
  // Disables the floating action bar identifying a user as a guest and asking them to RSVP via legacy RSVP
  disableWebActionBar,
  // disable the website theme popover to hide wedding specific content
  disableWebThemePopover,
  // Enable custom link option in general messaging (general and carbon only, not weddings)
  generalMessageCustomLink,
  // disables the overview onboarding carousel in the website overview page
  hideWebOverviewCarousel,
  // use pro icon navigation in hamburger
  menu,
  // Use general/carbon event message tracking logic (distinguishes from wedding events)
  messageTracking,
  // Skips every Messaging+ paywall and the header badge: Carbon always, general events until the generalEventMessagingPlus rollout
  messagingPlusExempt,
  // Compose-time intent detection in General Messaging — surfaces a Cash Funds nudge when the draft semantically asks for money
  messageIntentDetection,
  // use pro messaging with joy pro email templates (for general events and weddings)
  messaging,
  // use the new pro admin dashboard
  proDashboard,
  // enable registry in settings and navigation and UX cross sell
  registry,
  // Wedding websites have special logic around the about page always being first
  // General events are all custom pages and can have any page first
  reorderAllWebsitePages,
  // General events do not use firebase or legacy API.  Attempting to sync contacts with firebase will throw errors
  skipContactFirebaseSync,
  // enable stationery in navigation and UX cross sell
  stationery,
  // enable ticketing dashboard for Carbon events
  tickets,
  // Enable carbon scoped settings(carbon only)
  useCarbonScopedSettings,
  // Enable carbon scoped website editor (overview, photos, pages, etc..) (carbon only)
  useCarbonScopedWebsiteEditor,
  // Disconnects a carbon created event from the carbon workspace and uses the pro admin dashboard instead
  useCarbonWorkspace,
  //people picker from weddings, support tags
  useLegacyPeoplePicker,
  // Enable cash fund component in website editor (weddings only, requires newCashFundsEnabled + websiteCashFundComponentEnabled)
  websiteCashFundComponent,
  // Expose the Pro version of website custom components
  websiteComponents,
  // Use general/carbon naming for website features (distinguishes from wedding events)
  websiteFeatureNaming,
  // Use pro website header config to avoid wedding content
  websiteHeaderConfig,
  // Enables setting the website preview photo from general settings
  websitePhotoSetting,
  // Use the pro version of the website theme gallery which removes wedding designs and imagery
  websiteThemes,
}

/** @internal Only exported for unit testing. */
export type FeatureEvaluatorArgs = {
  readonly isGeneral: boolean;
  readonly isWedding: boolean;
  readonly isCarbonEnabled: boolean;
  readonly isModernNav: boolean;
  readonly disableCarbonWorkspaceRouting: boolean;
  readonly isWeddingGeneralMessagingEnabled: boolean;
  readonly isNewCashFundsEnabled: boolean;
  readonly isWebsiteCashFundComponentEnabled: boolean;
  readonly isMessageIntentDetectionEnabled: boolean;
  readonly isGeneralEventMessagingPlusEnabled: boolean;
};

const anyOptionTrue = ({ isGeneral, isCarbonEnabled, isModernNav }: FeatureEvaluatorArgs) => isGeneral || isCarbonEnabled || isModernNav;

/** @internal Only exported for unit testing. */
export const featureEvaluators: Readonly<Record<EventFeature, (args: FeatureEvaluatorArgs) => boolean>> = {
  [EventFeature.messaging]: ({ isGeneral, isWedding, isCarbonEnabled, isWeddingGeneralMessagingEnabled }) => {
    if (isGeneral || isCarbonEnabled) {
      return true;
    }
    if (isWedding) {
      return isWeddingGeneralMessagingEnabled;
    }
    return false;
  },
  [EventFeature.adminInvite]: args => anyOptionTrue(args),
  [EventFeature.carbonEnabled]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.cashFundMessaging]: ({ isWedding, isNewCashFundsEnabled }) => isWedding && isNewCashFundsEnabled,
  [EventFeature.messageIntentDetection]: ({ isWedding, isCarbonEnabled, isMessageIntentDetectionEnabled }) => isWedding && !isCarbonEnabled && isMessageIntentDetectionEnabled,
  [EventFeature.disableWebActionBar]: ({ isGeneral }) => isGeneral,
  [EventFeature.disableLegacyRsvp]: ({ isGeneral }) => isGeneral,
  [EventFeature.disableWebThemePopover]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableLegacyWebFeatures]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableSettingPersonalInfo]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.disableAddAppPage]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.generalMessageCustomLink]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.hideWebOverviewCarousel]: args => anyOptionTrue(args),
  [EventFeature.menu]: args => anyOptionTrue(args),
  [EventFeature.messageTracking]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.messagingPlusExempt]: ({ isGeneral, isCarbonEnabled, isGeneralEventMessagingPlusEnabled }) => isCarbonEnabled || (isGeneral && !isGeneralEventMessagingPlusEnabled),
  [EventFeature.proDashboard]: args => anyOptionTrue(args),
  [EventFeature.skipContactFirebaseSync]: ({ isGeneral }) => isGeneral,
  [EventFeature.stationery]: ({ isGeneral, isCarbonEnabled }) => !isGeneral && !isCarbonEnabled,
  [EventFeature.tickets]: ({ isCarbonEnabled, isGeneral }) => isCarbonEnabled || isGeneral,
  [EventFeature.registry]: ({ isGeneral, isCarbonEnabled }) => !isGeneral && !isCarbonEnabled,
  [EventFeature.reorderAllWebsitePages]: ({ isGeneral }) => isGeneral,
  [EventFeature.useCarbonScopedSettings]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.useCarbonScopedWebsiteEditor]: ({ isCarbonEnabled }) => isCarbonEnabled,
  [EventFeature.useCarbonWorkspace]: ({ disableCarbonWorkspaceRouting }) => !disableCarbonWorkspaceRouting,
  [EventFeature.useLegacyPeoplePicker]: ({ isWedding, isWeddingGeneralMessagingEnabled }) => isWedding && isWeddingGeneralMessagingEnabled,
  [EventFeature.websiteFeatureNaming]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.websiteCashFundComponent]: ({ isGeneral, isCarbonEnabled, isNewCashFundsEnabled, isWebsiteCashFundComponentEnabled }) =>
    !isGeneral && !isCarbonEnabled && isNewCashFundsEnabled && isWebsiteCashFundComponentEnabled,
  [EventFeature.websiteComponents]: args => anyOptionTrue(args),
  [EventFeature.websiteHeaderConfig]: ({ isGeneral, isCarbonEnabled }) => isGeneral || isCarbonEnabled,
  [EventFeature.websitePhotoSetting]: args => anyOptionTrue(args),
  [EventFeature.websiteThemes]: ({ isGeneral }) => isGeneral,
};

const evaluateFeature = (
  feature: EventFeature,
  isGeneral: boolean,
  isWedding: boolean,
  isCarbonEnabled: boolean,
  isModernNav: boolean,
  disableCarbonWorkspaceRouting: boolean,
  isWeddingGeneralMessagingEnabled: boolean,
  isNewCashFundsEnabled: boolean,
  isWebsiteCashFundComponentEnabled: boolean,
  isMessageIntentDetectionEnabled: boolean,
  isGeneralEventMessagingPlusEnabled: boolean,
) => {
  const evaluator = featureEvaluators[feature];
  if (!evaluator) {
    return false;
  }

  return evaluator({
    isGeneral,
    isWedding,
    isCarbonEnabled,
    isModernNav,
    disableCarbonWorkspaceRouting,
    isWeddingGeneralMessagingEnabled,
    isNewCashFundsEnabled,
    isWebsiteCashFundComponentEnabled,
    isMessageIntentDetectionEnabled,
    isGeneralEventMessagingPlusEnabled,
  });
};

export type ProFeatureState = Readonly<{
  /** The evaluated feature. `false` while `loading` is true — read `loading` before trusting it. */
  enabled: boolean;
  /**
   * True until every input the evaluators read has resolved: the event type (from event info), the
   * event feature flags (`useCarbon` etc.), and the remote feature flags. While loading, every
   * feature evaluates as if the event were a wedding with no Carbon and every flag off, so a
   * consumer that gates a side effect (auto-opening a paywall, firing telemetry) on the answer
   * must wait for this to flip.
   */
  loading: boolean;
}>;

/**
 * Loading-aware variant of `useProFeature`. Use it when a wrong-then-corrected answer has a cost —
 * a one-shot effect, an impression event, a redirect — rather than just a re-render.
 * @param feature The feature flag to evaluate using event type and remote feature flags.
 */
export const useProFeatureState = (feature: EventFeature): ProFeatureState => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventType = eventInfo?.eventType;

  // No request of its own: `Event.featureFlags` already rides along on `BasicEventInfo`, which
  // resolves earlier in the same load, so these are normally settled on the first render.
  const { loading: loadingEventFeatureFlags, isEventFeatureFlagEnabled } = useEventFeatureFlagsContext();
  const isCarbonEnabled = loadingEventFeatureFlags ? false : isEventFeatureFlagEnabled('useCarbon');
  const useModernNavigation = loadingEventFeatureFlags ? false : isEventFeatureFlagEnabled('useModernNavigation');
  const disableCarbonWorkspaceRouting = loadingEventFeatureFlags ? false : isEventFeatureFlagEnabled('disableCarbonWorkspaceRouting');
  const isGeneral = eventType === EventType.general;
  const isWedding = eventType === EventType.wedding;

  const [isWeddingGeneralMessagingEnabled, loadingWeddingGeneralMessaging] = useIsFeatureEnabled('weddingGeneralMessagingEnabled');
  const [isNewCashFundsEnabled, loadingNewCashFunds] = useIsFeatureEnabled('newCashFundsEnabled');
  const [isWebsiteCashFundComponentEnabled, loadingWebsiteCashFundComponent] = useIsFeatureEnabled('websiteCashFundComponentEnabled');
  const [isMessageIntentDetectionEnabled, loadingMessageIntentDetection] = useIsFeatureEnabled('messageIntentDetectionEnabled');
  const [isGeneralEventMessagingPlusEnabled, loadingGeneralEventMessagingPlus] = useIsFeatureEnabled('generalEventMessagingPlus');

  const enabled = evaluateFeature(
    feature,
    isGeneral,
    isWedding,
    isCarbonEnabled,
    useModernNavigation,
    disableCarbonWorkspaceRouting,
    isWeddingGeneralMessagingEnabled,
    isNewCashFundsEnabled,
    isWebsiteCashFundComponentEnabled,
    isMessageIntentDetectionEnabled,
    isGeneralEventMessagingPlusEnabled,
  );

  // Every input the evaluators read: event info (`!eventType` covers the window where there is a
  // handle but no data yet, which the provider does not always report as `loading`), the event
  // feature flags, and each remote flag. A remote flag still resolving reads as `false`, so a
  // feature like `cashFundMessaging` would otherwise report settled-and-off a beat too early.
  const loading =
    loadingEventInfo ||
    !eventType ||
    loadingEventFeatureFlags ||
    loadingWeddingGeneralMessaging ||
    loadingNewCashFunds ||
    loadingWebsiteCashFundComponent ||
    loadingMessageIntentDetection ||
    loadingGeneralEventMessagingPlus;

  return { enabled, loading };
};

/**
 * Determines whether the provided event feature should be enabled for the current event.
 * Resolves to `false` while the inputs are still loading; use `useProFeatureState` when that
 * transient answer matters.
 * @param feature The feature flag to evaluate using event type and remote feature flags.
 */
export const useProFeature = (feature: EventFeature): boolean => useProFeatureState(feature).enabled;
